import { HOW_TO_EARN_POINTS_MODAL_MODULE_ID } from 'root/src/shared/descriptions/modules/moduleIds';
import { colorsNew } from 'root/src/client/web/commonStyles';
import { LinkTermTypesEnum } from 'root/src/client/v2/drop/components/SponsorshipTerms/types';
import { createLinkParametrizedIndex, } from 'root/src/client/v2/drop/components/SponsorshipTerms/helpers';
import { PointsApplicationState } from 'root/src/server/modules/points/types/types';
import { getDateWithTimezone } from 'root/src/shared/util/getDateWithTimezone';
import * as moment from 'moment';
import { getClampedEstimatedPoints } from 'root/src/client/v2/_pages/drop/redesign/dropPoints.helper';
export const isPointsApplicationRejected = (dropEvent) => {
    return dropEvent.pointsApplicationState === PointsApplicationState.REJECTED;
};
// The bullets legacy and the redesign share verbatim. Split out of `sponsorshipRegularTerms` so the
// redesign can reuse them while overriding only the stream-date and reservation bullets. Legacy keeps
// the exact same list contents and order as before, see `sponsorshipRegularTerms` below.
export const sponsorshipBaseTerms = [
    {
        text: 'Put “#daredrop” in the stream title.',
        color: colorsNew.teal,
    },
    {
        color: colorsNew.primary,
        text: `Pin ${createLinkParametrizedIndex(0)} as a message in chat or add as chatbot command.`,
        links: [{ linkType: LinkTermTypesEnum.SHORT_LINK }],
        visibilityCondition: ({ shortUrl }) => Boolean(shortUrl),
    },
    {
        color: colorsNew.primary,
        text: 'Link to (link created after you join) in chat.',
        visibilityCondition: ({ shortUrl }) => !shortUrl,
    },
    {
        text: ({ dropEvent, pointsReservationDueDate }) => {
            if (typeof dropEvent.pointsStreamLimit === 'undefined') {
                return '';
            }
            const isReserved = Boolean(pointsReservationDueDate);
            const estimatedPoints = isReserved
                ? dropEvent.estimatedPointsToEarn
                : getClampedEstimatedPoints(dropEvent);
            if (!estimatedPoints) {
                return '';
            }
            return `The points you earn for this drop are based on your most viewed ${dropEvent.pointsStreamLimit} minutes of content. Using your average viewership, you will earn up to ${estimatedPoints === null || estimatedPoints === void 0 ? void 0 : estimatedPoints.toLocaleString()} points.`;
        },
        color: colorsNew.yellow,
        visibilityCondition: ({ canEarnPoints, dropEvent }) => {
            return Boolean(canEarnPoints) && (dropEvent.pointsStreamLimit || 0) > 0 && !isPointsApplicationRejected(dropEvent);
        },
    },
    {
        text: 'You must accept a Dare before going live, otherwise points will not be earned.',
        color: colorsNew.pink,
        visibilityCondition: ({ dropEvent }) => !isPointsApplicationRejected(dropEvent),
    },
];
export const sponsorshipStreamDateTerm = {
    text: ({ streamDate }) => `You have scheduled to stream on ${getDateWithTimezone(streamDate)}.`,
    color: colorsNew.teal,
    visibilityCondition: ({ streamDate, dropEvent }) => Boolean(streamDate) && !isPointsApplicationRejected(dropEvent),
};
export const buildSponsorshipReservationTerm = ({ includeExpiryConsequence = true } = {}) => ({
    text: ({ dropEvent, isTermsAccepted, pointsReservationDueDate }) => {
        const withConsequence = (base, consequence) => (includeExpiryConsequence ? `${base} ${consequence}` : base);
        const { estimatedPointsToEarn, pointsApplicationState, pointsReservationHours, isCreatorPointsAllowlisted, } = dropEvent;
        const reservedPoints = getClampedEstimatedPoints(dropEvent);
        const isDropLive = dropEvent.isLive;
        const isApplicationUnresolved = pointsApplicationState === undefined || pointsApplicationState === PointsApplicationState.PENDING;
        if (isApplicationUnresolved && !isCreatorPointsAllowlisted && !isDropLive) {
            const startPending = getDateWithTimezone(dropEvent.liveDate);
            const endPending = getDateWithTimezone(moment.utc(dropEvent.liveDate).add(pointsReservationHours, 'hours').format());
            return `Once your application will be approved and drop goes live, Dare Drop will reserve up to ${reservedPoints.toLocaleString()} points for you for ${pointsReservationHours} hours - from ${startPending} to ${endPending}.`;
        }
        if (isApplicationUnresolved && !isCreatorPointsAllowlisted && isDropLive) {
            return `Once your application will be approved, Dare Drop will reserve up to ${reservedPoints.toLocaleString()} points for you for ${pointsReservationHours} hours.`;
        }
        const start = getDateWithTimezone(dropEvent.liveDate);
        const end = pointsReservationDueDate
            ? getDateWithTimezone(moment.utc(pointsReservationDueDate).format())
            : getDateWithTimezone(moment.utc(dropEvent.liveDate).add(pointsReservationHours, 'hours').format());
        if (isDropLive) {
            if (isTermsAccepted) {
                return withConsequence(`Your points are reserved from ${start} to ${end}.`, 'After that, you can no longer earn points for this Drop.');
            }
            return `Once you accept these terms, Dare Drop will reserve up to ${reservedPoints.toLocaleString()} points for you for ${pointsReservationHours} hours.`;
        }
        return withConsequence(`Once this drop goes live, Dare Drop will reserve up to ${estimatedPointsToEarn.toLocaleString()} points for you for ${pointsReservationHours} hours - from ${start} to ${end}.`, 'After that, points are rewarded on a first come - first serve basis only to creators approved for this Drop, who did not meet their streaming deadline.');
    },
    color: colorsNew.green,
    visibilityCondition: ({ canEarnPoints, isTermsAccepted, canUserReclaimReservation, dropEvent }) => {
        if (isPointsApplicationRejected(dropEvent)) {
            return false;
        }
        return Boolean(canEarnPoints && (!isTermsAccepted || Boolean(canUserReclaimReservation)))
            || Boolean(canEarnPoints && isTermsAccepted);
    },
});
// Legacy list. Contents and order are exactly as before the split above, so the legacy modal renders
// identically: base bullets, then the stream-date bullet, then the reservation bullet with its
// trailing "After that, ..." wording.
export const sponsorshipRegularTerms = [
    ...sponsorshipBaseTerms,
    sponsorshipStreamDateTerm,
    buildSponsorshipReservationTerm(),
];
export const howToEarnPointsModal = {
    [HOW_TO_EARN_POINTS_MODAL_MODULE_ID]: {
        moduleType: 'modal',
        modalTitle: 'Drop Parameters',
        modalText: ({ dropEvent }) => {
            if (dropEvent.isLive) {
                return 'Welcome to our new points program! Here\'s what you need to do to earn points on this drop:';
            }
            return 'Welcome to our new points program! Your participation will only count when the drop is live. Here\'s what you need to do to earn points for this drop:';
        },
        modalContent: {
            buttonText: 'CONTINUE',
            regularTerms: sponsorshipRegularTerms,
            whitelistedTerms: [
                {
                    text: ({ dropEvent }) => `You are a partner of ${dropEvent.brand.brandName}, so you won't get points on this drop`,
                    color: colorsNew.teal,
                },
                {
                    text: 'But you can still earn Dare rewards until the Dares expire.',
                    color: colorsNew.pink,
                },
            ],
            termsAgreementText: 'I agree to the terms for earning Points on this Drop.',
        },
    },
};
